import { Navigate, useLocation } from 'react-router';

import type { BillingTabKey } from '@/app/router/paths';
import { Button } from '@/design-system';
import { hasRole, useSessionStore } from '@/shared/api';
import { HeaderTabs, Screen, ScreenEyebrow } from '@/shared/ui';

import { billingTabAt, billingTabForHash, billingTabsFor, type BillingTab } from './billing-tabs';
import { billingCopy, billingTabsCopy } from './copy';
import { useBillingIntegration, useMonthSummary } from './hooks';
import { useInvoiceBatch, type InvoiceBatch } from './tabs/invoice-batch';
import { InvoicesTab } from './tabs/InvoicesTab';
import { PlanTab } from './tabs/PlanTab';
import { PlatformInvoicesTab } from './tabs/PlatformInvoicesTab';
import { TariffsTab } from './tabs/TariffsTab';

const TAB_TITLE_ID = 'billing-tab-title';

/**
 * Scherm 10 - Facturatie & tarieven.
 *
 * Alles stond eerst als kaarten door elkaar op één pagina, en klanten zagen niet
 * meer wat van wie was. Nu één donkere kop met tabbladen, net als Instellingen
 * (`billing-tabs.ts`): eerst wat het bedrijf aan Smartkeur betaalt (Abonnement &
 * tegoed, Facturen van Smartkeur), dan wat zijn klanten aan het bedrijf betalen
 * (Facturen aan klanten, Tarieven). Elk tabblad is een eigen adres, heeft één zin
 * over wat er staat, en vraagt alleen zijn eigen gegevens op.
 *
 * Abonnement & tegoed staat op `/facturatie` zelf. Daar wijzen de API, de mails
 * en de betaalpagina heen: `#abonnement`, `#keuringen-kopen` en
 * `?betaling={uuid}` blijven zo gewoon werken. Een anker op een ander adres opent
 * het tabblad waarop het staat. Wie geen eigenaar is ziet de tabbladen over
 * Smartkeur niet, en komt op `/facturatie` op het eerste tabblad dat hij wél ziet.
 * Een onbekend pad gaat ook daarheen, en hoofdletters of een slash aan het eind
 * worden het adres van het tabblad - steeds zonder stap in de geschiedenis.
 *
 * De knop "alles versturen" staat in de kop, maar alleen op Facturen aan klanten:
 * daar horen de concepten bij. Ook de maand en de boekhoudkoppeling in de
 * eyebrow gaan over die facturen.
 */
export function BillingPage() {
    const session = useSessionStore((state) => state.session);
    const isOwner = hasRole(session, ['owner']);
    const { pathname, search, hash } = useLocation();

    const tabs = billingTabsFor(isOwner);
    const current = billingTabAt(tabs, pathname);
    const onInvoices = current?.key === 'invoices';

    /* Hooks staan vóór elke omleiding; ze vragen alleen iets op op Facturen aan klanten. */
    const batch = useInvoiceBatch(isOwner && onInvoices);
    const summary = useMonthSummary(onInvoices);
    const integration = useBillingIntegration(onInvoices);

    const anchored = billingTabForHash(tabs, hash);

    if (anchored && anchored.key !== current?.key) {
        return <Navigate to={{ pathname: anchored.to, search, hash }} replace />;
    }

    const first = tabs[0];

    if (first && !current) {
        return <Navigate to={{ pathname: first.to, search }} replace />;
    }

    if (current && pathname !== current.to) {
        return <Navigate to={{ pathname: current.to, search, hash }} replace />;
    }

    const eyebrow = onInvoices
        ? billingCopy.eyebrow(
              summary.data?.periodLabel ?? billingCopy.eyebrowLoading,
              integration.data ?? null,
          )
        : '';

    return (
        <Screen>
            <header className="shrink-0 bg-[var(--ink-800)] px-7 pt-5">
                <div className="flex flex-wrap items-end justify-between gap-5 [row-gap:12px]">
                    <div className="min-w-0">
                        <ScreenEyebrow text={eyebrow} className="mb-[6px]" />
                        <h1 className="m-0 font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--text-on-dark)]">
                            {billingCopy.title}
                        </h1>
                    </div>
                    {onInvoices && isOwner && batch.draftTotal > 0 ? (
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                icon="send"
                                disabled={batch.sendBatch.isPending}
                                onClick={() => batch.setConfirming(true)}
                            >
                                {billingCopy.sendAll(batch.draftTotal)}
                            </Button>
                        </div>
                    ) : null}
                </div>
                <HeaderTabs label={billingCopy.tabsLabel} tabs={tabs} className="mt-3" />
            </header>

            {/* Een eigen werkvlak per tabblad: een ander tabblad begint bovenaan. */}
            <div
                key={current?.key ?? 'leeg'}
                className="min-h-0 flex-1 overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8"
            >
                {current ? (
                    <section aria-labelledby={TAB_TITLE_ID} className="@container">
                        <TabIntro tab={current} />
                        <TabContent tab={current.key} isOwner={isOwner} batch={batch} />
                    </section>
                ) : null}
            </div>
        </Screen>
    );
}

/** De kop van het open tabblad, met één zin over wat er staat. */
function TabIntro({ tab }: { tab: BillingTab }) {
    return (
        <div className="mb-[18px] max-w-[640px]">
            <h2
                id={TAB_TITLE_ID}
                className="m-0 font-[family-name:var(--font-display)] text-[17px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {tab.label}
            </h2>
            <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {billingTabsCopy[tab.key].intro}
            </p>
        </div>
    );
}

/** De kaarten van het open tabblad. Alleen dat tabblad vraagt zijn gegevens op. */
function TabContent({
    tab,
    isOwner,
    batch,
}: {
    tab: BillingTabKey;
    isOwner: boolean;
    batch: InvoiceBatch;
}) {
    switch (tab) {
        case 'plan':
            return <PlanTab />;
        case 'platformInvoices':
            return <PlatformInvoicesTab />;
        case 'invoices':
            return <InvoicesTab canSend={isOwner} batch={batch} />;
        case 'tariffs':
            return <TariffsTab canManage={isOwner} />;
    }
}
